import React, { createContext, useContext, useEffect, useState } from 'react'
import { getCurrentUser, signIn, signUp, signOut, confirmSignUp, confirmSignIn, AuthUser } from 'aws-amplify/auth'
import { publicApi } from '../services/api'
import toast from 'react-hot-toast'

interface AuthContextType {
    user: AuthUser | null
    loading: boolean
    needsPasswordChange: boolean
    pendingEmail: string
    signIn: (email: string, password: string) => Promise<void>
    signUp: (email: string, password: string, firstName: string, lastName: string, companyName?: string, usageDescription?: string) => Promise<void>
    signOut: () => Promise<void>
    confirmSignUp: (email: string, code: string) => Promise<void>
    confirmNewPassword: (newPassword: string) => Promise<void>
    clearPasswordChange: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

interface AuthProviderProps {
    children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<AuthUser | null>(null)
    const [loading, setLoading] = useState(true)
    const [needsPasswordChange, setNeedsPasswordChange] = useState(false)
    const [pendingEmail, setPendingEmail] = useState('')
    const [pendingUserData, setPendingUserData] = useState<{
        firstName: string
        lastName: string
        companyName?: string
        usageDescription?: string
    } | null>(null)

    useEffect(() => {
        checkAuthState()
    }, [])

    async function checkAuthState() {
        try {
            const currentUser = await getCurrentUser()
            setUser(currentUser)
        } catch (error) {
            setUser(null)
        } finally {
            setLoading(false)
        }
    }

    const handleSignIn = async (email: string, password: string) => {
        try {
            const result = await signIn({ username: email, password })

            if (result.isSignedIn) {
                const currentUser = await getCurrentUser()
                setUser(currentUser)

                try {
                    await publicApi.registerUser(currentUser.userId, email)
                } catch (error) {
                    // User already registered or registration failed — non-blocking
                }

                toast.success('Successfully signed in!')
            } else if (result.nextStep) {
                switch (result.nextStep.signInStep) {
                    case 'CONFIRM_SIGN_UP':
                        toast.error('Please verify your email first. Check your inbox for a verification code.')
                        throw new Error('Email verification required')

                    case 'RESET_PASSWORD':
                        toast.error('Password reset required. Please reset your password.')
                        throw new Error('Password reset required')

                    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                        setPendingEmail(email)
                        setNeedsPasswordChange(true)
                        toast('Please set a new password to continue.', { icon: 'ℹ️' })
                        return

                    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                        toast.error('MFA verification required.')
                        throw new Error('MFA required')

                    default:
                        toast.error('Additional verification required. Please check your email or contact support.')
                        throw new Error('Additional verification required')
                }
            } else {
                toast.error('Sign in incomplete. Please try again.')
                throw new Error('Sign in incomplete')
            }
        } catch (error: any) {
            console.error('❌ Sign in error:', error)

            // Handle specific Cognito errors
            if (error.name === 'UserNotConfirmedException') {
                toast.error('Please verify your email first. Check your inbox for a verification code.')
            } else if (error.name === 'NotAuthorizedException') {
                toast.error('Invalid email or password. Please try again.')
            } else if (error.name === 'UserNotFoundException') {
                toast.error('User not found. Please check your email or sign up.')
            } else if (error.name === 'TooManyRequestsException') {
                toast.error('Too many attempts. Please wait a moment and try again.')
            } else if (error.name === 'PasswordResetRequiredException') {
                toast.error('Password reset required. Please reset your password.')
            } else if (!error.message?.includes('New password required')) {
                toast.error(error.message || 'Failed to sign in')
            }

            if (!needsPasswordChange) {
                throw error
            }
        }
    }

    const confirmNewPassword = async (newPassword: string) => {
        try {
            const result = await confirmSignIn({ challengeResponse: newPassword })

            if (result.isSignedIn) {
                const currentUser = await getCurrentUser()
                setUser(currentUser)
                setNeedsPasswordChange(false)
                setPendingEmail('')

                try {
                    await publicApi.registerUser(currentUser.userId, pendingEmail)
                } catch (error) {
                    // Non-blocking
                }

                toast.success('Password updated and signed in successfully!')
            }
        } catch (error: any) {
            console.error('❌ Password confirmation error:', error)
            toast.error(error.message || 'Failed to update password')
            throw error
        }
    }

    const clearPasswordChange = () => {
        setNeedsPasswordChange(false)
        setPendingEmail('')
        setPendingUserData(null)
    }

    const handleSignUp = async (email: string, password: string, firstName: string, lastName: string, companyName?: string, usageDescription?: string) => {
        try {
            await signUp({
                username: email,
                password,
                options: {
                    userAttributes: {
                        email,
                        given_name: firstName,
                        family_name: lastName,
                    },
                },
            })
            
            try {
                const currentUser = await getCurrentUser()
                await publicApi.registerUser(
                    currentUser.userId, 
                    email,
                    firstName,
                    lastName,
                    companyName,
                    usageDescription
                )
            } catch (error) {
                // Non-blocking
            }
            
            toast.success('Account created successfully!')
        } catch (error: any) {
            console.error('❌ Sign up error:', error)

            if (error.name === 'UsernameExistsException') {
                toast.error('An account with this email already exists. Please sign in instead.')
            } else if (error.name === 'InvalidPasswordException') {
                toast.error('Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, and numbers.')
            } else {
                toast.error(error.message || 'Failed to create account')
            }
            throw error
        }
    }

    const handleSignOut = async () => {
        try {
            await signOut()
            setUser(null)
            setNeedsPasswordChange(false)
            setPendingEmail('')
            toast.success('Successfully signed out!')
        } catch (error: any) {
            console.error('❌ Sign out error:', error)
            toast.error(error.message || 'Failed to sign out')
            throw error
        }
    }

    const handleConfirmSignUp = async (email: string, code: string) => {
        try {
            await confirmSignUp({ username: email, confirmationCode: code })

            try {
                const currentUser = await getCurrentUser()
                await publicApi.registerUser(
                    currentUser.userId,
                    email,
                    pendingUserData?.firstName,
                    pendingUserData?.lastName,
                    pendingUserData?.companyName,
                    pendingUserData?.usageDescription
                )
                setPendingUserData(null)
            } catch (error) {
                // Non-blocking
            }

            toast.success('Email verified! You can now sign in.')
        } catch (error: any) {
            console.error('❌ Confirmation error:', error)

            if (error.name === 'CodeMismatchException') {
                toast.error('Invalid verification code. Please check and try again.')
            } else if (error.name === 'ExpiredCodeException') {
                toast.error('Verification code has expired. Please request a new one.')
            } else {
                toast.error(error.message || 'Failed to verify email')
            }
            throw error
        }
    }

    const value = {
        user,
        loading,
        needsPasswordChange,
        pendingEmail,
        signIn: handleSignIn,
        signUp: handleSignUp,
        signOut: handleSignOut,
        confirmSignUp: handleConfirmSignUp,
        confirmNewPassword,
        clearPasswordChange,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}